import { z } from 'zod'

/**
 * Central error registry — PRD §13.2, §13.4, B16.
 * Every entry maps a stable code to UX contract fields required by the error envelope.
 */

export const ErrorEnvelope = z
  .object({
    code: z.string().min(1),
    message_key: z.string().min(1),
    retryable: z.boolean(),
    recovery_action: z.string().min(1),
    correlation_id: z.string().min(1).optional(),
    current_revision: z.number().int().nonnegative().optional(),
    field_errors: z
      .array(
        z.object({
          path: z.string(),
          message_key: z.string(),
        }),
      )
      .optional(),
  })
  .strict()
export type ErrorEnvelope = z.infer<typeof ErrorEnvelope>

export interface ErrorDefinition {
  /** Stable machine code (storage/evidence safe). */
  code: string
  /** i18n key for the title/explanation. */
  message_key: string
  retryable: boolean
  /** Immediate corrective action key — never "something went wrong" alone (ADD-004). */
  recovery_action: string
  /** Optional default HTTP status for API mapping — §13.4 proposed mappings. */
  http_status?: number
  /** Documentation help article slug (optional). */
  help_article?: string
}

function def(
  code: string,
  message_key: string,
  retryable: boolean,
  recovery_action: string,
  http_status?: number,
  help_article?: string,
): ErrorDefinition {
  return http_status === undefined
    ? { code, message_key, retryable, recovery_action, ...(help_article ? { help_article } : {}) }
    : {
        code,
        message_key,
        retryable,
        recovery_action,
        http_status,
        ...(help_article ? { help_article } : {}),
      }
}

/** §13.2 product error codes. */
export const PRODUCT_ERROR_CODES = [
  def(
    'FREE_ACTIVE_LIMIT',
    'error.free_active_limit',
    false,
    'error.recovery.wait_void_upgrade',
    409,
  ),
  def(
    'FREE_SIGNER_LIMIT',
    'error.free_signer_limit',
    false,
    'error.recovery.reduce_signers_or_upgrade',
    409,
  ),
  def(
    'DELIVERY_TRIAL_USED',
    'error.delivery_trial_used',
    false,
    'error.recovery.share_it_myself_or_upgrade',
    409,
  ),
  def(
    'NO_DELIVERY_CREDITS',
    'error.no_delivery_credits',
    false,
    'error.recovery.share_it_myself_or_buy_pack',
    409,
  ),
  def('LINK_EXPIRED', 'error.link_expired', false, 'error.recovery.create_new_request', 410),
  def(
    'ENVELOPE_TERMINAL',
    'error.envelope_terminal',
    false,
    'error.recovery.view_current_state',
    409,
  ),
  // "Send again" made one replacement for a retired preparation already; a second copy of the same
  // restarted draft can never become a second request (CreateEnvelopeRequest.replaces_envelope_id).
  def(
    'ENVELOPE_ALREADY_REPLACED',
    'error.envelope_already_replaced',
    false,
    'error.recovery.view_current_state',
    409,
  ),
  def(
    'RECIPIENT_ALREADY_SUBMITTED',
    'error.recipient_already_submitted',
    false,
    'error.recovery.refresh_state',
    409,
  ),
  def(
    'IDEMPOTENCY_CONFLICT',
    'error.idempotency_conflict',
    false,
    'error.recovery.retry_support_code',
    409,
  ),
  def(
    'PURCHASE_ALREADY_APPLIED',
    'error.purchase_already_applied',
    false,
    'error.recovery.refresh_balance',
    409,
  ),
  def(
    'PURCHASE_PENDING',
    'error.purchase_pending',
    true,
    'error.recovery.refresh_or_reconcile',
    409,
  ),
  def(
    'BACKUP_AUTH_REVOKED',
    'error.backup_auth_revoked',
    false,
    'error.recovery.reconnect_destination',
    409,
  ),
  def(
    'BACKUP_DESTINATION_UNAVAILABLE',
    'error.backup_destination_unavailable',
    true,
    'error.recovery.retry_or_change_location',
    409,
  ),
  /*
   * S5.5 operational edits (§6.4–§6.6; AT-015/016, RMT-12, MCP-AC-128, STM-03/10).
   *
   * These are the codes the sender app's own pre-call gates already name
   * (`apps/web/src/lib/request-operations.ts`), and the `message_key`s are the keys that file
   * renders, so a refusal predicted locally and the same refusal returned by the service
   * display one sentence. Where an existing code already carries the client's meaning
   * (`RECIPIENT_ALREADY_SUBMITTED`, `ENVELOPE_TERMINAL`, `LINK_EXPIRED`, `PLAN_LIMIT` with a
   * `limitation.*` field error) it is reused instead of being duplicated here.
   */
  def(
    'CORRECTION_REQUIRES_LIFETIME',
    'request.correction_requires_lifetime',
    false,
    'error.recovery.upgrade_or_void_and_recreate',
    409,
  ),
  def(
    'RESEND_LIMIT_REACHED',
    'request.resend_limit',
    false,
    'error.recovery.share_the_existing_link',
    409,
  ),
  def('RESEND_TOO_SOON', 'request.resend_too_soon', true, 'error.recovery.wait_then_resend', 409),
  def(
    'EXTENSION_REQUIRES_LIFETIME',
    'request.extension_requires_lifetime',
    false,
    'error.recovery.upgrade_or_new_request',
    409,
  ),
  def(
    'RESPONSES_ACCEPTED',
    'request.extension_after_acceptance',
    false,
    'error.recovery.create_new_request',
    409,
  ),
  def(
    'EXTENSION_NOT_LONGER',
    'request.extension_must_lengthen',
    false,
    'error.recovery.choose_later_deadline',
    422,
  ),
  def(
    'CC_READ_REQUIRES_COMMIT',
    'request.cc_read_after_commit',
    false,
    'error.recovery.wait_for_completion',
    409,
  ),
] as const satisfies readonly ErrorDefinition[]

/** B16 MCP tool error codes. */
export const MCP_ERROR_CODES = [
  def('AUTH_REQUIRED', 'error.mcp.auth_required', false, 'error.recovery.start_oauth', 401),
  def(
    'SCOPE_REQUIRED',
    'error.mcp.scope_required',
    false,
    'error.recovery.incremental_authorization',
    403,
  ),
  def('PLAN_LIMIT', 'error.mcp.plan_limit', false, 'error.recovery.exact_blocker_alternative', 409),
  def(
    'REVIEW_REQUIRED',
    'error.mcp.review_required',
    false,
    'error.recovery.prepare_and_confirm',
    409,
  ),
  def('REVISION_CONFLICT', 'error.mcp.revision_conflict', false, 'error.recovery.reprepare', 409),
  def(
    'ACTIVE_LIMIT',
    'error.mcp.active_limit',
    false,
    'error.recovery.preserve_draft_manage_active',
    409,
  ),
  def(
    'DELIVERY_CREDIT_REQUIRED',
    'error.mcp.delivery_credit_required',
    false,
    'error.recovery.manual_share_or_purchase_if_permitted',
    409,
  ),
  def(
    'ARTIFACT_UNAVAILABLE',
    'error.mcp.artifact_unavailable',
    true,
    'error.recovery.local_backup_or_wait',
    404,
  ),
  def(
    'HOST_CAPABILITY_UNSUPPORTED',
    'error.mcp.host_capability_unsupported',
    false,
    'error.recovery.browser_continuation_or_alternative',
    400,
  ),
  def(
    'RETRYABLE_PROVIDER',
    'error.mcp.retryable_provider',
    true,
    'error.recovery.retry_same_key',
    503,
  ),
  def(
    'TERMINAL_STATE',
    'error.mcp.terminal_state',
    false,
    'error.recovery.return_current_state',
    409,
  ),
  // SM4's two manual-share states, each of which would be a lie under another name: a host that
  // asks for the share package before the person has finished the browser step has *not* hit a
  // host-policy limit (HOST_CAPABILITY_UNSUPPORTED) or a terminal lifecycle (TERMINAL_STATE) —
  // the action is simply not complete yet; and an envelope the service delivers has no manual
  // link at all, which is a different fact from either.
  def(
    'BROWSER_COMPLETION_REQUIRED',
    'error.mcp.browser_completion_required',
    true,
    'error.recovery.wait_for_completion',
    409,
  ),
  def(
    'MANUAL_SHARE_UNAVAILABLE',
    'error.mcp.manual_share_unavailable',
    false,
    'error.recovery.view_current_state',
    409,
  ),
] as const satisfies readonly ErrorDefinition[]

/**
 * OP-prefixed technical codes — PDF / delivery / purchase / backup / finalization (§13.2).
 * Namespaces: OP_PDF_*, OP_DELIVERY_*, OP_PURCHASE_*, OP_BACKUP_*, OP_FINALIZE_*.
 */
export const TECHNICAL_ERROR_CODES = [
  // PDF / import (PDF-02..07, PDF-11)
  def(
    'OP_PDF_ENCRYPTED',
    'error.op.pdf.encrypted',
    false,
    'error.recovery.remove_password_or_export_plain',
    422,
  ),
  def(
    'OP_PDF_MALFORMED',
    'error.op.pdf.malformed',
    false,
    'error.recovery.choose_another_file',
    422,
  ),
  def(
    'OP_PDF_ZERO_PAGES',
    'error.op.pdf.zero_pages',
    false,
    'error.recovery.choose_another_file',
    422,
  ),
  def(
    'OP_PDF_ACTIVE_CONTENT',
    'error.op.pdf.active_content',
    false,
    'error.recovery.flattened_export_guidance',
    422,
  ),
  def(
    'OP_PDF_EXISTING_SIGNATURE',
    'error.op.pdf.existing_signature',
    false,
    'error.recovery.invalidate_or_reject',
    422,
  ),
  def(
    'OP_PDF_TOO_LARGE',
    'error.op.pdf.too_large',
    false,
    'error.recovery.split_or_reduce_file',
    413,
  ),
  def(
    'OP_PDF_TOO_MANY_PAGES',
    'error.op.pdf.too_many_pages',
    false,
    'error.recovery.split_document',
    413,
  ),
  def(
    'OP_PDF_IMAGE_PIXEL_LIMIT',
    'error.op.pdf.image_pixel_limit',
    false,
    'error.recovery.downscale_image',
    413,
  ),
  def(
    'OP_PDF_IMAGE_UNSUPPORTED',
    'error.op.pdf.image_unsupported',
    false,
    'error.recovery.export_to_pdf_locally',
    415,
  ),
  def(
    'OP_PDF_COMPOSE_FAILED',
    'error.op.pdf.compose_failed',
    true,
    'error.recovery.retry_compose',
    500,
  ),
  def(
    'OP_PDF_TEXT_EXTRACT_FAILED',
    'error.op.pdf.text_extract_failed',
    true,
    'error.recovery.retry_or_manual_fields',
    500,
  ),
  // Delivery / email
  def(
    'OP_DELIVERY_PROVIDER_UNAVAILABLE',
    'error.op.delivery.provider_unavailable',
    true,
    'error.recovery.retry_backoff',
    503,
  ),
  def(
    'OP_DELIVERY_SUPPRESSED',
    'error.op.delivery.suppressed',
    false,
    'error.recovery.manual_share',
    409,
  ),
  def(
    'OP_DELIVERY_EMAIL_REQUIRED',
    'error.op.delivery.email_required',
    false,
    'error.recovery.manual_share',
    422,
  ),
  def(
    'OP_DELIVERY_BUDGET_EXCEEDED',
    'error.op.delivery.budget_exceeded',
    false,
    'error.recovery.wait_or_manual',
    429,
  ),
  def(
    'OP_DELIVERY_CLAIM_LOST',
    'error.op.delivery.claim_lost',
    true,
    'error.recovery.none_required',
    409,
  ),
  // Purchase
  def(
    'OP_PURCHASE_VERIFICATION_FAILED',
    'error.op.purchase.verification_failed',
    false,
    'error.recovery.contact_support',
    422,
  ),
  def(
    'OP_PURCHASE_RECEIPT_INVALID',
    'error.op.purchase.receipt_invalid',
    false,
    'error.recovery.retry_or_contact_support',
    422,
  ),
  def(
    'OP_PURCHASE_REVOKED',
    'error.op.purchase.revoked',
    false,
    'error.recovery.restore_or_contact_support',
    409,
  ),
  // Service lifecycle
  def('OP_NOT_IMPLEMENTED', 'error.op.not_implemented', false, 'error.recovery.none_required', 501),
  def(
    'OP_AUTH_CHALLENGE_INVALID',
    'error.op.auth.challenge_invalid',
    true,
    'error.recovery.request_new_code',
    401,
  ),
  def(
    'OP_CONFIGURATION_GATE_UNSET',
    'error.op.configuration_gate_unset',
    false,
    'error.recovery.contact_support',
    503,
  ),
  def(
    'OP_IDEMPOTENCY_IN_FLIGHT',
    'error.op.idempotency_in_flight',
    true,
    'error.recovery.retry_same_request',
    409,
  ),
  // Backup / storage
  def(
    'OP_BACKUP_QUOTA_EXCEEDED',
    'error.op.backup.quota_exceeded',
    true,
    'error.recovery.free_space_or_change_location',
    507,
  ),
  def(
    'OP_BACKUP_WRITE_FAILED',
    'error.op.backup.write_failed',
    true,
    'error.recovery.retry_backup',
    500,
  ),
  def(
    'OP_BACKUP_CORRUPT_ARCHIVE',
    'error.op.backup.corrupt_archive',
    false,
    'error.recovery.choose_another_backup',
    422,
  ),
  def(
    'OP_BACKUP_AUTH_FAILED',
    'error.op.backup.auth_failed',
    false,
    'error.recovery.check_backup_passphrase',
    401,
  ),
  def(
    'OP_BACKUP_VERSION_UNSUPPORTED',
    'error.op.backup.version_unsupported',
    false,
    'error.recovery.update_app_for_backup',
    422,
  ),
  def(
    'OP_STORAGE_QUOTA_EXCEEDED',
    'error.op.storage.quota_exceeded',
    false,
    'error.recovery.free_local_space',
    507,
  ),
  def(
    'OP_STORAGE_MISSING_BYTES',
    'error.op.storage.missing_bytes',
    false,
    'error.recovery.restore_from_backup',
    404,
  ),
  def(
    'OP_STORAGE_CORRUPT_REVISION',
    'error.op.storage.corrupt_revision',
    false,
    'error.recovery.recover_previous_revision',
    409,
  ),
  def(
    'OP_STORAGE_REVISION_CONFLICT',
    'error.op.storage.revision_conflict',
    true,
    'error.recovery.reload_and_merge',
    409,
  ),
  // Finalization
  def(
    'OP_FINALIZE_LEASE_EXPIRED',
    'error.op.finalize.lease_expired',
    true,
    'error.recovery.reprepare_finalize',
    409,
  ),
  def(
    'OP_FINALIZE_STALE_LEASE',
    'error.op.finalize.stale_lease',
    false,
    'error.recovery.reprepare_finalize',
    409,
  ),
  def(
    'OP_FINALIZE_CONFLICT',
    'error.op.finalize.conflict',
    false,
    'error.recovery.refresh_and_retry',
    409,
  ),
  def(
    'OP_FINALIZE_DEADLINE_PASSED',
    'error.op.finalize.deadline_passed',
    false,
    'error.recovery.incomplete_evidence_export',
    410,
  ),
  def(
    'OP_FINALIZE_RESPONSE_SET_INCOMPLETE',
    'error.op.finalize.response_set_incomplete',
    false,
    'error.recovery.wait_for_signers',
    409,
  ),
  // Attachments (D10, §14.2). The read of an attachment's bytes is a reviewed key-access
  // decision, so a deployment that has not recorded the review refuses with its own code rather
  // than pretending the file does not exist.
  def(
    'OP_FORBIDDEN_ATTACHMENT_ACCESS',
    'error.op.attachment.access_forbidden',
    false,
    'error.recovery.not_authorized',
    403,
  ),
  def(
    'OP_ATTACHMENT_UPLOAD_EXPIRED',
    'error.op.attachment.upload_expired',
    true,
    'error.recovery.retry_same_request',
    410,
  ),
  // Generic transport / validation (§13.4)
  def(
    'OP_INVALID_PAYLOAD',
    'error.op.invalid_payload',
    false,
    'error.recovery.fix_field_errors',
    400,
  ),
  def('OP_UNAUTHENTICATED', 'error.op.unauthenticated', false, 'error.recovery.sign_in', 401),
  // The session is real but not *recent* enough for an operation that always reauthenticates
  // (account export, account deletion, purchase reconciliation — S6.3). Distinct from
  // `OP_UNAUTHENTICATED`: the caller is signed in and must prove the identity again, not sign in
  // for the first time.
  def(
    'OP_REAUTHENTICATION_REQUIRED',
    'error.op.reauthentication_required',
    false,
    'error.recovery.sign_in_again',
    401,
  ),
  def('OP_FORBIDDEN', 'error.op.forbidden', false, 'error.recovery.not_authorized', 403),
  def('OP_NOT_FOUND', 'error.op.not_found', false, 'error.recovery.view_available_items', 404),
  def('OP_REVISION_MISMATCH', 'error.op.revision_mismatch', false, 'error.recovery.reload', 409),
  def('OP_SIZE_LIMIT', 'error.op.size_limit', false, 'error.recovery.reduce_payload', 413),
  def('OP_RATE_LIMITED', 'error.op.rate_limited', true, 'error.backoff_retry', 429),
  def(
    'OP_SERVICE_UNAVAILABLE',
    'error.op.service_unavailable',
    true,
    'error.recovery.retry_later',
    503,
  ),
] as const satisfies readonly ErrorDefinition[]

/** Complete registry used by clients and services. */
export const ERROR_REGISTRY = [
  ...PRODUCT_ERROR_CODES,
  ...MCP_ERROR_CODES,
  ...TECHNICAL_ERROR_CODES,
] as const

export type ErrorCode = (typeof ERROR_REGISTRY)[number]['code']

const registryByCode = new Map<string, ErrorDefinition>(ERROR_REGISTRY.map((e) => [e.code, e]))

export function getErrorDefinition(code: string): ErrorDefinition | undefined {
  return registryByCode.get(code)
}

export function isErrorDefinition(value: unknown): value is ErrorDefinition {
  if (typeof value !== 'object' || value === null) return false
  const v = value as Record<string, unknown>
  return (
    typeof v.code === 'string' &&
    typeof v.message_key === 'string' &&
    typeof v.retryable === 'boolean' &&
    typeof v.recovery_action === 'string'
  )
}

/** Build a canonical error envelope from a registered code. */
export function errorEnvelope(
  code: ErrorCode,
  extras?: Omit<ErrorEnvelope, 'code' | 'message_key' | 'retryable' | 'recovery_action'>,
): ErrorEnvelope {
  const entry = registryByCode.get(code)
  if (!entry) {
    throw new Error(`Unknown error code: ${code}`)
  }
  return {
    code: entry.code,
    message_key: entry.message_key,
    retryable: entry.retryable,
    recovery_action: entry.recovery_action,
    ...extras,
  }
}
